/**
 * 音频剪辑节点
 * 
 * 功能说明：
 * 剪辑音频片段，支持设置开始/结束时间
 * 
 * 连接说明：
 * - 输入：音频参考节点或音频任务节点
 * - 输出：音频任务节点
 */
import React, { memo, useState, useCallback } from 'react';
import { useReactFlow, useStore, Handle, Position } from '@xyflow/react';
import { nanoid } from 'nanoid';
import { useProject } from '../providers/ProjectProvider';
import { useNodeOperations } from '../providers/NodeOperationsProvider';
import ffmpegService from '../../../api/FFmpegService';
import ApiService from '../../../api/ApiService';
import './AudioTrimNode.css';

const apiService = new ApiService();

export const AudioTrimNode = memo(({ id, data }) => {
  const { updateNodeData, getNode } = useReactFlow();
  const { addNodesWithEdges, duplicateNode } = useNodeOperations();
  const edges = useStore((state) => state.edges);
  const { readOnly } = useProject();

  const [start, setStart] = useState(data.start ?? 0);
  const [end, setEnd] = useState(data.end ?? '');
  const [outputFormat, setOutputFormat] = useState(data.outputFormat || 'mp3');

  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusText, setStatusText] = useState('');
  const [error, setError] = useState(null);

  // 获取连接的音频节点
  const getConnectedAudioUrl = useCallback(() => {
    // 查找连接到 audio handle 的边
    const connectedEdges = edges.filter(edge => {
      if (edge.target !== id) return false;
      // 如果有 targetHandle，必须是 'audio'
      if (edge.targetHandle) {
        return edge.targetHandle === 'audio';
      }
      // 没有 targetHandle 的情况下，接受音频类型节点
      const sourceNode = getNode(edge.source);
      const validTypes = ['audioReference', 'audioTask', 'textToAudio', 'hailuoMusic', 'sunoMusic'];
      return sourceNode && validTypes.includes(sourceNode.type);
    });

    if (connectedEdges.length === 0) return null;

    const sourceNode = getNode(connectedEdges[0].source);
    if (!sourceNode) return null;

    // 从音频参考节点获取音频
    if (sourceNode.type === 'audioReference') {
      if (sourceNode.data?.audioUrl) {
        console.log('[AudioTrim] 找到音频参考节点，音频URL:', sourceNode.data.audioUrl);
        return sourceNode.data.audioUrl;
      }
    }

    // 从音频任务节点获取生成的音频
    if (sourceNode.type === 'audioTask') {
      if (sourceNode.data?.audioUrl) {
        console.log('[AudioTrim] 找到音频任务节点，音频URL:', sourceNode.data.audioUrl);
        return sourceNode.data.audioUrl;
      }
    }

    // 从语音合成节点获取音频（textToAudio 会创建 audioTask 节点，这里处理直接连接的情况）
    if (sourceNode.type === 'textToAudio' || sourceNode.type === 'hailuoMusic' || sourceNode.type === 'sunoMusic') {
      // 这些节点本身不存储音频，它们会创建 audioTask 节点
      // 但如果用户直接连接，我们检查是否有输出的音频URL
      if (sourceNode.data?.audioUrl) {
        console.log('[AudioTrim] 找到音频生成节点，音频URL:', sourceNode.data.audioUrl);
        return sourceNode.data.audioUrl;
      }
    }

    return null;
  }, [edges, getNode, id]);

  // 复制节点
  const handleDuplicate = (e) => {
    e.stopPropagation();
    duplicateNode(id);
  };

  // 执行剪辑
  const handleProcess = async () => {
    try {
      setError(null);
      setIsProcessing(true);
      setProgress(0);
      setStatusText('正在获取音频...');

      console.log('[AudioTrim] 开始剪辑任务');
      console.log('[AudioTrim] 当前连接的边:', edges.filter(e => e.target === id));

      // 1. 获取音频 URL
      const audioUrl = getConnectedAudioUrl();
      if (!audioUrl) {
        throw new Error('请先连接音频源');
      }

      console.log('[AudioTrim] 音频URL:', audioUrl);

      // 验证参数
      if (start < 0) {
        throw new Error('开始时间不能小于0');
      }

      const endVal = end === '' ? null : parseFloat(end);
      if (endVal !== null && endVal <= start) {
        throw new Error('结束时间必须大于开始时间');
      }

      // 2. 加载 FFmpeg
      setStatusText('正在加载 FFmpeg...');
      setProgress(10);
      await ffmpegService.load((p) => {
        setProgress(10 + Math.round(p * 20));
        setStatusText(`加载 FFmpeg: ${Math.round(p * 100)}%`);
      });

      // 3. 执行剪辑
      setStatusText('正在剪辑音频...');
      setProgress(40);

      const result = await ffmpegService.trimAudio(audioUrl, {
        start: parseFloat(start),
        end: endVal,
        outputFormat,
      });

      setProgress(70);
      setStatusText('正在上传...');

      // 4. 上传到 R2
      const fileName = `audio_trim_${Date.now()}.${result.outputFormat}`;
      const file = new File([result.blob], fileName, { type: result.type });
      const uploadResult = await apiService.uploadImageToR2(file);

      setProgress(90);
      setStatusText('正在创建结果节点...');

      // 5. 创建 audioTask 节点显示结果
      const taskNodeId = `audioTask_${nanoid()}`;
      const currentNode = getNode(id);
      const newNode = {
        id: taskNodeId,
        type: 'audioTask',
        position: {
          x: currentNode.position.x + 350,
          y: currentNode.position.y,
        },
        data: {
          audioUrl: uploadResult.public_url,
          status: 'completed',
          taskType: 'audio-trim',
          inputParams: {
            start,
            end: endVal,
            outputFormat: result.outputFormat,
          },
        },
      };

      const newEdge = {
        id: nanoid(),
        source: id,
        sourceHandle: 'output',
        target: taskNodeId,
        type: 'default',
      };

      addNodesWithEdges([newNode], [newEdge]);

      // 清理 blob URL
      URL.revokeObjectURL(result.url);

      setProgress(100);
      setStatusText('剪辑完成！');

      setTimeout(() => {
        setIsProcessing(false);
        setStatusText('');
        setProgress(0);
      }, 1500);

    } catch (err) {
      console.error('[AudioTrim] 剪辑失败:', err);
      setError(err.message || '剪辑失败');
      setIsProcessing(false);
      setStatusText('');
      setProgress(0);
    }
  };

  // 检查是否有音频连接
  const hasAudioConnection = getConnectedAudioUrl() !== null;

  return (
    <div className="AudioTrimNode-container">
      {/* 复制按钮 */}
      <button
        className="AudioTrimNode-duplicate-button"
        onClick={handleDuplicate}
        title="复制节点"
      >
        <i className="bi bi-copy"></i>
      </button>

      {/* 音频输入 Handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="audio"
        className="AudioTrimNode-handle AudioTrimNode-handle-audio"
        style={{ top: '50%' }}
      />

      {/* 输出 Handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="output"
        className="AudioTrimNode-handle AudioTrimNode-handle-output"
        style={{ top: '50%' }}
      />

      {/* 标题栏 */}
      <div className="AudioTrimNode-title">音频剪辑</div>

      {/* 内容区域 */}
      <div className="AudioTrimNode-wrapper">
        {/* 连接状态显示 */}
        <div className="AudioTrimNode-status">
          <div className="AudioTrimNode-status-item">
            <span className="AudioTrimNode-status-label">音频输入：</span>
            <span className={`AudioTrimNode-status-value ${hasAudioConnection ? 'AudioTrimNode-status-connected' : 'AudioTrimNode-status-disconnected'}`}>
              {hasAudioConnection ? '✓ 已连接' : '✗ 未连接'}
            </span>
          </div>
        </div>

        {/* 参数配置 */}
        <div className="AudioTrimNode-config">
          <div className="AudioTrimNode-row">
            <div className="AudioTrimNode-field">
              <label>开始 (秒)</label>
              <input
                type="number"
                min="0"
                step="0.1"
                value={start}
                onChange={(e) => setStart(parseFloat(e.target.value) || 0)}
                disabled={isProcessing || readOnly}
              />
            </div>
            <div className="AudioTrimNode-field">
              <label>结束 (秒)</label>
              <input
                type="number"
                min="0"
                step="0.1"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                placeholder="到结尾"
                disabled={isProcessing || readOnly}
              />
            </div>
          </div>
          <div className="AudioTrimNode-field">
            <label>输出格式</label>
            <select
              value={outputFormat}
              onChange={(e) => setOutputFormat(e.target.value)}
              disabled={isProcessing || readOnly}
            >
              <option value="mp3">MP3</option>
              <option value="m4a">M4A</option>
            </select>
          </div>
        </div>

        {/* 进度条 */}
        {isProcessing && (
          <div className="AudioTrimNode-progress">
            <div className="AudioTrimNode-progress-bar">
              <div
                className="AudioTrimNode-progress-fill"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="AudioTrimNode-progress-text">{statusText}</div>
          </div>
        )}

        {/* 错误提示 */}
        {error && (
          <div className="AudioTrimNode-error">{error}</div>
        )}

        {/* 操作按钮 */}
        <button
          className={`AudioTrimNode-btn ${isProcessing || !hasAudioConnection || readOnly ? 'AudioTrimNode-btn-disabled' : ''}`}
          onClick={handleProcess}
          disabled={isProcessing || !hasAudioConnection || readOnly}
        >
          {isProcessing ? '处理中...' : '开始剪辑'}
        </button>
      </div>
    </div>
  );
});

AudioTrimNode.displayName = 'AudioTrimNode';
